import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, API } from "../lib/api";
import { Crown, Sparkles, Search, X } from "lucide-react";
import { hideOnError } from "../lib/imgFallback";

/**
 * Public Elder Founders gallery — `/elders`.
 * Showcases all 500 founding practitioners with rank, photo + name.
 * Includes search + tradition / category filters so visitors can find
 * a relevant practitioner among the founder cohort. Filters can be
 * deep-linked via URL params (?q=, ?tradition=, ?category=) — e.g. the
 * Landing-page TraditionHub tiles funnel here with a pre-seeded q.
 */
export default function Elders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ total: 0, unfilteredTotal: 0, cap: 500, remaining: 500, elders: [], facets: { traditions: [], categories: [] } });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [tradition, setTradition] = useState(searchParams.get("tradition") || null);
  const [category, setCategory] = useState(searchParams.get("category") || null);

  // Debounce q so we don't fire on every keystroke.
  const params = useMemo(() => ({ q: q || undefined, tradition: tradition || undefined, category: category || undefined }), [q, tradition, category]);

  // Keep URL in sync — visitors can share /elders?q=tarot bookmarks AND
  // the TraditionHub deep-links land on a pre-filtered grid.
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (tradition) next.set("tradition", tradition);
    if (category) next.set("category", category);
    setSearchParams(next, { replace: true });
  }, [q, tradition, category, setSearchParams]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/providers/elders", { params });
        setData(data);
      } catch { /* noop */ }
      setLoading(false);
    }, q ? 280 : 0);  // 280ms debounce only when user types
    return () => clearTimeout(t);
  }, [params, q]);

  // Dynamic SEO meta tags — every filter combination becomes its own
  // indexable surface. `/elders?q=hoodoo` shows up in search as "Hoodoo
  // practitioners on Cauldron · N verified Elders" rather than the
  // generic /elders title.
  useEffect(() => {
    const filtersActiveNow = q || tradition || category;
    const filterLabel = (q || tradition || category || "").trim();
    const totalForCopy = data.total ?? 0;

    let title;
    let desc;
    if (filtersActiveNow && filterLabel) {
      const niceLabel = filterLabel.charAt(0).toUpperCase() + filterLabel.slice(1);
      if (totalForCopy === 0) {
        title = `${niceLabel} practitioners on Cauldron · The 500 Founder Elders`;
        desc = `Cauldron's first 500 Founder Elders span dozens of spiritual traditions — ` +
               `diviners, rootworkers, astrologers, ceremonialists. Browse the full Elder Wall ` +
               `to discover a verified practitioner in your tradition.`;
      } else {
        title = `${niceLabel} practitioners · ${totalForCopy} verified Elder${totalForCopy === 1 ? "" : "s"} · Cauldron`;
        desc = `Browse ${totalForCopy} verified Founder Elder${totalForCopy === 1 ? "" : "s"} ` +
               `who practice ${niceLabel} on Cauldron, the social marketplace for spiritual ` +
               `practitioners. Each carries the founder badge for being among the first 500 ` +
               `seated in the coven.`;
      }
    } else {
      title = "The 500 Founder Elders · Cauldron";
      desc = "Browse the first 500 verified spiritual practitioners on Cauldron — diviners, " +
             "rootworkers, astrologers, ceremonialists, and lineage holders. Each Founder carries " +
             "the permanent gold ring and crown across the platform.";
    }

    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", window.location.href, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    return () => { document.title = "Cauldron"; };
  }, [q, tradition, category, data.total]);

  // schema.org CollectionPage + ItemList JSON-LD — makes Tradition Hub
  // URLs appear as rich list-style results in Google instead of plain
  // blue-link results. Updates in-place as filters change.
  useEffect(() => {
    if (!data.elders) return;
    const filterLabelInner = (q || tradition || category || "").trim();
    const niceLabelInner = filterLabelInner
      ? filterLabelInner.charAt(0).toUpperCase() + filterLabelInner.slice(1)
      : "";
    const pageName = filterLabelInner
      ? `${niceLabelInner} practitioners on Cauldron`
      : "The 500 Founder Elders of Cauldron";
    const pageDesc = filterLabelInner
      ? `Verified Founder Elders specializing in ${niceLabelInner} on Cauldron, the social marketplace for spiritual practitioners.`
      : "The first 500 verified spiritual practitioners on Cauldron — diviners, rootworkers, astrologers, ceremonialists, and lineage holders.";

    const items = (data.elders || []).slice(0, 20).map((e, i) => {
      const elderName = e.businessName || e.name || `Founder #${e.elderIndex}`;
      const elderUrl = `${window.location.origin}/elders/${e.userId}`;
      const img = e.avatarUrl
        ? (e.avatarUrl.startsWith("http") ? e.avatarUrl : `${API}${e.avatarUrl}`)
        : undefined;
      return {
        "@type": "ListItem",
        position: i + 1,
        url: elderUrl,
        item: {
          "@type": "Person",
          "@id": elderUrl,
          name: elderName,
          jobTitle: "Spiritual Practitioner",
          url: elderUrl,
          ...(img ? { image: img } : {}),
          ...(e.tradition ? { knowsAbout: [e.tradition] } : {}),
        },
      };
    });

    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": window.location.href,
          url: window.location.href,
          name: pageName,
          description: pageDesc,
          inLanguage: "en",
          isPartOf: {
            "@type": "WebSite",
            name: "Cauldron",
            url: window.location.origin,
          },
          mainEntity: {
            "@type": "ItemList",
            name: pageName,
            numberOfItems: data.total ?? 0,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            itemListElement: items,
          },
        },
      ],
    };

    let script = document.getElementById("elders-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "elders-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(graph);

    return () => {
      const s = document.getElementById("elders-jsonld");
      if (s) s.remove();
    };
  }, [q, tradition, category, data.elders, data.total]);

  // Progress bar binds to the UNFILTERED cohort total so it stays stable
  // when the user is searching/filtering. `data.total` reflects the
  // filtered count and feeds the results-count chip instead.
  const cohortTotal = data.unfilteredTotal ?? data.total;
  const progressPct = Math.min(100, (cohortTotal / data.cap) * 100);
  const cohortRemaining = data.unfilteredTotal != null
    ? Math.max(0, data.cap - data.unfilteredTotal)
    : data.remaining;
  const filtersActive = q || tradition || category;
  const clearFilters = () => { setQ(""); setTradition(null); setCategory(null); };

  return (
    <Layout>
      <div className="space-y-10" data-testid="elders-page">
        {/* Hero */}
        <div className="text-center">
          <div className="label-mystic flex items-center justify-center gap-2 text-secondary">
            <Crown size={12} strokeWidth={1.5} /> 500 founders · One coven
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-tight">
            The <em className="text-secondary not-italic" style={{ background: "linear-gradient(135deg,#D95C14 0%,#C5A059 50%,#F4D58E 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Elder Founders</em>
          </h1>
          <p className="text-foreground/70 max-w-2xl mx-auto mt-4 leading-relaxed">
            The first five hundred practitioners to stand in the Cauldron. Each carries the permanent gold ring and the founder&apos;s crown across every page of the platform — a record of who lit the first fire.
          </p>
        </div>

        {/* Progress bar — bound to the UNFILTERED cohort, not the current
            filter result, so the founder cohort total stays accurate when
            the user is searching. */}
        <div className="parchment p-6">
          <div className="flex justify-between items-end mb-3">
            <div>
              <div className="label-mystic text-secondary">Initiated</div>
              <div className="font-serif text-4xl mt-1" data-testid="elders-total">
                {cohortTotal} <span className="text-foreground/40 text-2xl">/ {data.cap}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="label-mystic">Seats Remaining</div>
              <div className="font-serif text-3xl text-secondary mt-1" data-testid="elders-remaining">{cohortRemaining}</div>
            </div>
          </div>
          <div className="h-2 bg-secondary/10 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${progressPct}%`,
                background: "linear-gradient(90deg,#D95C14 0%,#C5A059 50%,#F4D58E 100%)",
                boxShadow: "0 0 20px rgba(217,92,20,0.55)",
              }}
              data-testid="elders-progress"
            />
          </div>
          {cohortRemaining > 0 && (
            <p className="text-xs text-foreground/55 mt-3">
              Subscribe as a practitioner today to claim one of the remaining {cohortRemaining} founder seats — the badge is permanent and minted only once.
              {" "}
              <Link to="/auth?mode=register" className="text-secondary underline" data-testid="elders-cta-signup">Become a practitioner →</Link>
            </p>
          )}
        </div>

        {/* Search + Filters */}
        <div className="parchment p-4 space-y-3" data-testid="elders-filters">
          <div className="flex items-center gap-3">
            <Search size={14} className="text-secondary shrink-0" strokeWidth={1.75} />
            <input
              type="text"
              placeholder="Search by name, tradition, or lineage…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none border-b border-secondary/15 focus:border-secondary/50 transition-colors py-2"
              data-testid="elders-search-input"
            />
            {filtersActive && (
              <button
                onClick={clearFilters}
                className="text-[11px] uppercase tracking-[0.18em] text-foreground/60 hover:text-secondary inline-flex items-center gap-1 shrink-0"
                data-testid="elders-clear-filters"
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>

          {data.facets.categories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="label-mystic text-foreground/55 mr-1">Specialty</span>
              {data.facets.categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(category === c ? null : c)}
                  className={`text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm transition-colors ${
                    category === c
                      ? "bg-secondary text-background"
                      : "border border-secondary/30 text-foreground/75 hover:border-secondary/60"
                  }`}
                  data-testid={`elders-filter-category-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {data.facets.traditions.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="label-mystic text-foreground/55 mr-1">Tradition</span>
              {data.facets.traditions.slice(0, 12).map((t) => (
                <button
                  key={t}
                  onClick={() => setTradition(tradition === t ? null : t)}
                  className={`text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm transition-colors ${
                    tradition === t
                      ? "bg-primary text-primary-foreground"
                      : "border border-primary/25 text-foreground/75 hover:border-primary/55"
                  }`}
                  data-testid={`elders-filter-tradition-${t.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {t}
                </button>
              ))}
              {data.facets.traditions.length > 12 && (
                <span className="text-[11px] text-foreground/45">+{data.facets.traditions.length - 12} more</span>
              )}
            </div>
          )}

          {filtersActive && !loading && (
            <div className="text-xs text-foreground/55 italic pt-1" data-testid="elders-results-count">
              {data.total} match{data.total === 1 ? "" : "es"}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="parchment p-12 text-center font-serif text-2xl text-foreground/60">Gathering the founders…</div>
        ) : data.elders.length === 0 ? (
          <div className="parchment p-12 text-center text-foreground/60" data-testid="elders-empty-state">
            {filtersActive
              ? "No founders match these filters. Try clearing them or broadening your search."
              : "No founders yet — be the first."}
          </div>
        ) : (
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))" }}
            data-testid="elders-grid"
          >
            {data.elders.map((e) => (
              <ElderCard key={e.userId} elder={e} />
            ))}
          </div>
        )}

        {/* Footer flourish */}
        <div className="text-center pt-4">
          <Sparkles size={16} strokeWidth={1.25} className="mx-auto text-secondary/40" />
          <p className="text-xs text-foreground/40 uppercase tracking-[0.32em] mt-2">
            The book of founders · sealed at 500
          </p>
        </div>
      </div>
    </Layout>
  );
}

function ElderCard({ elder: e }) {
  const initials = (e.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const avatarSrc = e.avatarUrl?.startsWith("http") ? e.avatarUrl : (e.avatarUrl ? `${API}${e.avatarUrl}` : null);
  return (
    <Link
      to={`/elders/${e.userId}`}
      className="group block parchment p-4 hover:translate-y-[-2px] transition-transform"
      data-testid={`elder-card-${e.userId}`}
    >
      <div className="flex flex-col items-center text-center">
        <div className="relative mb-3">
          {/* gold conic ring */}
          <div
            className="absolute pointer-events-none rounded-full"
            style={{
              inset: "-6px",
              background: "conic-gradient(from 140deg, #C5A059 0deg, #D95C14 90deg, #F4D58E 180deg, #C5A059 270deg, #D95C14 360deg)",
              padding: "3px",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              boxShadow: "0 0 14px rgba(217,92,20,0.45)",
            }}
          />
          <div className="relative w-20 h-20 rounded-full overflow-hidden bg-secondary/15 border-2 border-card flex items-center justify-center">
            {avatarSrc ? (
              <img src={avatarSrc} alt={e.name} className="w-full h-full object-cover" onError={hideOnError} />
            ) : (
              <span className="font-serif text-2xl text-secondary">{initials}</span>
            )}
          </div>
          <span
            className="absolute flex items-center justify-center rounded-full"
            style={{
              top: "-6px",
              right: "-6px",
              width: 24,
              height: 24,
              background: "linear-gradient(135deg,#D95C14 0%,#C5A059 60%,#F4D58E 100%)",
              color: "#15102A",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4), 0 0 0 2px #15102A",
            }}
          >
            <Crown size={12} strokeWidth={2.25} fill="currentColor" />
          </span>
        </div>
        <div className="font-serif text-lg leading-tight" data-testid={`elder-card-name-${e.userId}`}>
          {e.businessName || e.name}
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/50 mt-1">
          {e.elderIndex != null
            ? `#${String(e.elderIndex).padStart(4, "0")} of 500`
            : "Founder Elder"}
        </div>
        {e.category && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-secondary mt-1">{e.category}</div>
        )}
        {e.tradition && (
          <div className="text-[10px] text-foreground/55 mt-1 truncate w-full" title={e.tradition}>{e.tradition}</div>
        )}
      </div>
    </Link>
  );
}


// SEO meta helper — upserts a <meta> tag by name (or property) attribute.
function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
